import React, { useRef, useState } from "react";
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useReportBuilderStore } from "./store";
import { ReportGrain } from "./types";

const GRAIN_OPTIONS: { value: ReportGrain; label: string }[] = [
  { value: "grant", label: "Grant" },
  { value: "employee", label: "Employee" },
  { value: "plan", label: "Plan" },
  { value: "vesting_tranche", label: "Vesting Tranche" },
  { value: "transaction", label: "Transaction" },
];

export function SheetTabs() {
  const {
    config,
    activeSheetId,
    setActiveSheet,
    addSheet,
    removeSheet,
    renameSheet,
    setReportGrain,
  } = useReportBuilderStore();

  const activeSheet =
    config.sheets.find((s) => s.id === activeSheetId) ?? config.sheets[0];

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  function startRename(id: string, currentName: string) {
    setEditingId(id);
    setEditValue(currentName);
    setTimeout(() => inputRef.current?.select(), 0);
  }

  function commitRename() {
    if (editingId) {
      const trimmed = editValue.trim();
      if (trimmed) renameSheet(editingId, trimmed);
    }
    setEditingId(null);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") commitRename();
    if (e.key === "Escape") setEditingId(null);
  }

  return (
    <div className="flex items-center bg-white border-b border-borderColor flex-shrink-0">
      {/* Sheet tabs */}
      <div className="flex items-center overflow-x-auto">
        {config.sheets.map((sheet) => {
          const isActive = sheet.id === activeSheetId;
          const isEditing = editingId === sheet.id;

          return (
            <div
              key={sheet.id}
              className={`group flex items-center gap-1.5 px-3 py-2.5 border-b-2 cursor-pointer whitespace-nowrap text-xs transition-colors ${
                isActive
                  ? "border-primary text-primary font-semibold"
                  : "border-transparent text-secondaryText hover:text-primaryText hover:border-gray-300"
              }`}
              onClick={() => {
                if (!isEditing) setActiveSheet(sheet.id);
              }}
              onDoubleClick={() => startRename(sheet.id, sheet.name)}
            >
              {isEditing ? (
                <input
                  ref={inputRef}
                  className="w-24 text-xs border border-inputBorder rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onBlur={commitRename}
                  onKeyDown={handleKeyDown}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <span>{sheet.name}</span>
              )}
              {config.sheets.length > 1 && !isEditing && (
                <button
                  className="opacity-0 group-hover:opacity-100 transition-opacity ml-0.5 text-gray-400 hover:text-red-500"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeSheet(sheet.id);
                  }}
                  title="Remove sheet"
                >
                  <XMarkIcon className="w-3 h-3" />
                </button>
              )}
            </div>
          );
        })}

        <button
          onClick={addSheet}
          className="flex items-center gap-1 px-2 py-2.5 text-xs text-secondaryText hover:text-primary transition-colors flex-shrink-0"
          title="Add sheet"
        >
          <PlusIcon className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Grain selector — right side */}
      <div
        data-tour="report-basis"
        className="ml-auto flex items-center gap-2 px-4 flex-shrink-0 border-l border-borderColor"
      >
        <span className="text-[11px] text-secondaryText font-medium whitespace-nowrap">
          Report basis:
        </span>
        <select
          value={activeSheet?.reportGrain ?? "grant"}
          onChange={(e) => setReportGrain(e.target.value as ReportGrain)}
          className="text-xs border border-inputBorder rounded-md px-2 py-1 bg-white text-primaryText focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {GRAIN_OPTIONS.map((g) => (
            <option key={g.value} value={g.value}>
              {g.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
